import external from '../tools/base.external';
import { FALLBACK_LANGUAGES, normaliseLanguage } from './languages';

/**
 * Reads the language list the backend advertises from GET /settings/languages, which is public and
 * needs no session. The response is `{ default, languages: [{ code, name }] }` and each `name` is
 * the endonym, so the picker reads natively whatever the UI language is.
 *
 * The two sides bound different things and are deliberately combined rather than intersected:
 *
 *   - what the API advertises governs the locales it can render *notifications and email* in
 *     (assets/lang SUPPORTED), because writing a choice also sets the notification locale;
 *   - what this build ships governs the locales the *admin interface* can render.
 *
 * Taking the union keeps every language this build can actually display selectable even while the
 * API's catalog is still gated, and lets the server contribute names — and later, new languages we
 * ship resources for. A language the API does not accept still switches the interface; the write
 * back to the server fails and the choice stays on this device. See controller.user.updateLanguage.
 * @returns {Promise<Array<{code: string, name: string}>>}
 */
export async function fetchAvailableLanguages() {
    try {
        const response = await external.static_get(process.env.REACT_APP_API_PATH + 'settings/languages');
        const advertised = Array.isArray(response) ? response : (response && response.languages);

        if (!Array.isArray(advertised)) {
            return FALLBACK_LANGUAGES;
        }

        const namesByCode = new Map();

        advertised.forEach(entry => {
            const code = normaliseLanguage(entry && entry.code);

            if (code != null && entry.name) {
                namesByCode.set(code, entry.name);
            }
        });

        return FALLBACK_LANGUAGES.map(language => ({
            code: language.code,
            name: namesByCode.get(language.code) || language.name
        }));
    } catch (error) {
        return FALLBACK_LANGUAGES;
    }
}
